import React, { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import dayjs, { Dayjs } from 'dayjs';
import { DatePicker } from 'antd';
import type { CellRenderInfo } from 'rc-picker/es/interface';

import { DATE_API_FORMAT, TIME, FULL_DATE_TIME_FORMAT } from 'constants/date_formats';
import { PROPERTY_CHANNELS } from 'constants/property_channels';

import { IDateRangePickerProps, IDatesRange } from './date_range_picker.types';
import styles from './date_range_picker.module.scss';
import isCarProperty from 'utils/is_car_property';

const { RangePicker } = DatePicker;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const RangePickerAny = RangePicker as any;

const INPUT_CLASS_ACTIVE = 'ant-picker-input-active';

const DateRangePicker: React.FC<IDateRangePickerProps> = ({
  onCalendarChange,
  closedDates,
  defaultValue,
  className,
  renderNumOfNights,
  allowEmpty = [true, true],
  variant = 'outlined',
  disablePastDates = false,
  placeholder,
  size = 'middle',
  type = PROPERTY_CHANNELS.HOTEL,
  ranges,
  maxDate,
  maxDays,
  dateRange,
  closedDatesPerRate,
  showTime = false,
}) => {
  const dateOfPicker = document.getElementsByClassName('ant-picker-input');
  const isCar = isCarProperty(type);

  const getNumOfNights = (calendarDates = dates) => {
    if (!calendarDates || !calendarDates[0] || !calendarDates[1]) return 0;
    const daysDiff = Math.ceil(calendarDates[1].diff(calendarDates[0], 'minutes') / 60 / 24);
    return daysDiff;
  };

  const { t } = useTranslation();

  const [isPickerOpen, togglePicker] = useState<boolean>(false);
  const [dates, setDates] = useState<IDatesRange['dateRange']>(defaultValue || undefined);
  const [numberOfNights, setNumberOfNights] = useState(getNumOfNights());

  useEffect(() => {
    if (dateRange) {
      setDates(dateRange);
      togglePicker(false);
    }
  }, [dateRange]);

  const getIsDateClosed = useCallback(
    (date: Dayjs, ratePlanId?: string) => {
      if (ratePlanId && closedDatesPerRate) {
        const { closed } = closedDatesPerRate[ratePlanId];

        if (closed) return closed.includes(date.format(DATE_API_FORMAT));
        return false;
      } else if (closedDates) {
        const { closed } = closedDates;

        if (closed) return closed.includes(date.format(DATE_API_FORMAT));
        return false;
      }
      return false;
    },
    [closedDates, closedDatesPerRate],
  );

  const disabledDate = (date: Dayjs) => {
    const timeParam = isCar ? 'minutes' : 'days';
    if (getIsDateClosed(date)) return true;
    if (!disablePastDates) return false;
    if (dates?.[0] && dates?.[0].diff(date, timeParam) === 0) {
      return true;
    }
    if (dates?.[1] && dates?.[1].diff(date, timeParam) === 0) {
      return true;
    }

    if (maxDate && maxDate.diff(date, timeParam) <= 0) {
      return true;
    }

    if (maxDays && closedDatesPerRate) {
      let isClosedRange = false;
      for (const ratePlanId in closedDatesPerRate) {
        if (ratePlanId === 'type') {
          continue;
        }
        isClosedRange = false;

        for (let i = 0; i < maxDays; i++) {
          if (getIsDateClosed(dayjs(date).add(i, 'days'), ratePlanId)) {
            isClosedRange = true;
          }
        }
        // If an open range is found, no need to check other rate plans
        if (isClosedRange === false) {
          break;
        }
      }
      if (isClosedRange === true) {
        return true;
      }
    }

    if (maxDays && dateOfPicker?.[1]?.classList?.contains(INPUT_CLASS_ACTIVE) && dates?.[0] && dates?.[1]) {
      return true;
    }

    if (!maxDays && dateOfPicker?.[0]?.classList?.contains(INPUT_CLASS_ACTIVE) && dates?.[1]) {
      if (isDateClosedForCheckin(date)) {
        return true;
      }
    }

    if (!maxDays && dates?.[0] && dateOfPicker?.[1]?.classList?.contains(INPUT_CLASS_ACTIVE)) {
      if (isDateClosedForCheckout(date)) {
        return true;
      }
    }

    return 0 < dayjs().diff(dayjs(date), 'day');
  };

  const handleDateChange = (dates: IDateRangePickerProps['defaultValue']) => {
    setDates(dates);
    onCalendarChange(dates);
    setNumberOfNights(getNumOfNights(dates));
  };

  const compareDates = (from: Dayjs, to: Dayjs) => {
    let daysDiff = to.diff(from, 'day');
    if (isCar && to.diff(from, 'minute')) {
      daysDiff = daysDiff + 1;
    }
    if (0 <= daysDiff) return setNumberOfNights(daysDiff);
    return setNumberOfNights(0);
  };

  const handleDateHover = (date: Dayjs) => {
    if (dateOfPicker?.[0]?.classList?.contains(INPUT_CLASS_ACTIVE)) {
      if (!dates?.[1]) return;
      return compareDates(date, dates?.[1]);
    } else if (dateOfPicker[1].classList.contains(INPUT_CLASS_ACTIVE)) {
      if (!dates?.[0]) return;
      return compareDates(dates[0], date);
    }
  };

  const cellRender = React.useCallback((current: number | Dayjs, info: CellRenderInfo<Dayjs>) => {
    if (info.type !== 'date') {
      return info.originNode;
    }
    if (typeof current === 'number') {
      return <div className="ant-picker-cell-inner">{current}</div>;
    }
    return (
      <div
        className="ant-picker-cell-inner"
        onMouseEnter={() => handleDateHover(current)}
        onMouseLeave={() => setNumberOfNights(getNumOfNights())}
      >
        {current.date()}
      </div>
    );
  }, []);

  const renderFooter = () => {
    if (!renderNumOfNights) return null;
    return (
      <div className={styles.number_of_nights}>{`${t(`date_picker_${type}.duration_unit`)} ${numberOfNights}`} </div>
    );
  };

  const isDateClosedForCheckin = (date: Dayjs) => {
    if (0 < dayjs(dates?.[1]).diff(date, 'day') && closedDates?.closed) {
      const hasClosedDateBetween = closedDates?.closed.find(d => {
        return dayjs(d).isBefore(dayjs(dates?.[1])) && dayjs(date).isBefore(dayjs(d));
      });

      if (hasClosedDateBetween) {
        return true;
      }

      if (closedDatesPerRate) {
        let isClosedRange = true;

        for (const ratePlanId in closedDatesPerRate) {
          if (ratePlanId === 'type') {
            continue;
          }

          const hasClosedDate = closedDatesPerRate[ratePlanId].closed.find(
            d =>
              (dayjs(dates?.[0]).isBefore(dayjs(d)) || dayjs(dates?.[0]).isSame(dayjs(d))) &&
              dayjs(d).isBefore(dayjs(date)),
          );

          if (!hasClosedDate) {
            isClosedRange = false;
            break;
          }
        }
        return isClosedRange;
      }
    }

    return false;
  };

  const isDateClosedForCheckout = (date: Dayjs) => {
    if (0 < date.diff(dayjs(dates?.[0]), 'day') && closedDates?.closed) {
      const hasClosedDateBetween = closedDates?.closed.find(d => {
        return 0 < dayjs(d).diff(dayjs(dates?.[0]), 'day') && 0 < date.diff(dayjs(d), 'day');
      });

      if (hasClosedDateBetween) {
        return true;
      }

      if (closedDatesPerRate) {
        let isClosedRange = true;

        for (const ratePlanId in closedDatesPerRate) {
          if (ratePlanId === 'type') {
            continue;
          }

          const hasClosedDate = closedDatesPerRate[ratePlanId].closed.find(
            d =>
              (dayjs(dates?.[0]).isBefore(dayjs(d)) || dayjs(dates?.[0]).isSame(dayjs(d))) &&
              dayjs(d).isBefore(dayjs(date)),
          );
          if (!hasClosedDate) {
            isClosedRange = false;
            break;
          }
        }
        return isClosedRange;
      }
    }

    return false;
  };

  if (!maxDays && dates?.[0] && dates?.[1]) {
    const defaultDates: IDatesRange['dateRange'] = JSON.parse(JSON.stringify(dates));
    if (isDateClosedForCheckout(dates?.[1]) && defaultDates?.[1]) {
      defaultDates[1] = undefined;
    }
    if (isDateClosedForCheckin(dates?.[0]) && defaultDates?.[0]) {
      defaultDates[0] = undefined;
    }

    if (JSON.stringify(dates) !== JSON.stringify(defaultDates)) {
      handleDateChange(defaultDates);
    }
  }

  const defaultTimeOptions = {
    format: TIME,
    defaultValue: [dayjs('10:00', TIME), dayjs('10:00', TIME)],
    minuteStep: 30 as const,
  };

  return (
    <div data-testid="DateRangePicker">
      <RangePickerAny
        onChange={handleDateChange}
        allowEmpty={allowEmpty}
        variant={variant}
        disabledDate={disabledDate}
        defaultValue={defaultValue || undefined}
        value={dates}
        className={className}
        placeholder={placeholder || [t(`${type}_page.checkin_label`), t(`${type}_page.checkout_label`)]}
        renderExtraFooter={renderFooter}
        size={size}
        presets={ranges}
        cellRender={cellRender}
        inputReadOnly={true}
        open={isPickerOpen}
        onOpenChange={togglePicker}
        showTime={showTime ? defaultTimeOptions : false}
        format={showTime ? FULL_DATE_TIME_FORMAT : DATE_API_FORMAT}
      />
    </div>
  );
};

export default DateRangePicker;
